import React, {
  useContext,
  useCallback,
  useState,
  useEffect,
  useMemo,
} from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import Container from 'ls-common-client/dist/components/Container';
import SearchBar from '../../SearchBar';
import HeaderBannerChristmas from '../../../assets/header/headerBannerChristmas.png';
import { COOKIES } from '../../../constants';
import { Context } from '../../../context/AppContext';
import useScrollPosition from '../../../hooks/useScrollPosition';
import Divider from '../../../v2/components/UI/Divider';

const StyledRoot = styled(Container)`
  &.Header__wrapper--backgroundOverlay .Header__container {
    background-repeat: no-repeat;
    background-size: contain;
    background-position: left center;
  }

  &.Header__StateOfOrigin--QLD .Header__root {
    background-image: linear-gradient(267deg, #8e2752 80%, #620b3a 9%);
  }

  &.Header__StateOfOrigin--NSW .Header__root {
    background-image: linear-gradient(88deg, #1c3567 7%, #1a457d 79%);
  }

  &.Header__StateOfOrigin--NSW .StateOfOrigin--title span {
    color: #54ace4;
    margin-left: 1px;
  }

  &.Header__StateOfOrigin--QLD .StateOfOrigin--title span {
    color: #b89057;
    margin-left: 1px;
  }

  .StateOfOrigin--title {
    display: flex;
    align-items: center;
    margin: 0 20px;
  }

  .StateOfOrigin--title img {
    width: 22px;
  }

  .StateOfOrigin--title p {
    color: #ffffff;
    font-family: 'Circular';
    font-weight: bold;
    font-size: 16px;
    padding: 0;
    margin: 0 0 0 10px;
    text-shadow: 1px 2px rgba(0, 0, 0, 0.5);
  }

  @media (max-width: 767px) {
    &.Header__wrapper--backgroundOverlay .Header__container {
      background-size: cover;
    }

    &.Header__StateOfOrigin--NSW .Header__root {
      background-image: linear-gradient(258deg, #1c3567 81%, #1c3567 9%);
    }

    &.Header__StateOfOrigin--QLD .Header__root {
      background-image: linear-gradient(258deg, #8e2752 81%, #620b3a 9%);
    }
  }
`;

const StyledContainer = styled(Container)`
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-align-items: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  --page-padding: 1rem;
  margin-left: auto;
  margin-right: auto;
  padding-left: var(--page-padding);
  padding-right: var(--page-padding);
  max-width: 1440px;
  width: 100%;
  height: 60px;
  color: #4a4a4a;
  background-color: #ffffff;
`;

const { NOTIFICATION_ALERT_BANNER } = COOKIES;

const Wrapper = ({
  slideIn,
  className,
  style,
  christmas,
  children,
  onHeaderShow = () => {},
  onBannerShow = () => {},
}) => {
  const {
    media: { device, mobile },
    cookie: { getCookie },
  } = useContext(Context);
  const [bannerActive] = useState(
    getCookie(NOTIFICATION_ALERT_BANNER) !== 'false'
  );
  const [showHeader, setShowHeader] = useState(false);
  const [headerRef, setHeaderRef] = useState();
  const [headerHeight, setHeaderHeight] = useState(0);

  const callback = useCallback(() => {
    setShowHeader((slideIn || bannerActive) && window.scrollY > 44);
  }, [slideIn, bannerActive]);

  useScrollPosition(null, callback);

  useEffect(() => {
    const { current = {} } = headerRef || {};
    const timer = setTimeout(() => {
      if (bannerActive) {
        setHeaderHeight(current.offsetHeight);
      } else {
        setHeaderHeight(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [bannerActive, headerRef]);

  useEffect(() => {
    onHeaderShow(showHeader);
    return () => {};
  }, [showHeader]);

  useEffect(() => {
    onBannerShow(bannerActive);
    return () => {};
  }, [bannerActive]);

  const backgroundOverlay = useMemo(() => {
    if (christmas) return HeaderBannerChristmas;
    return null;
  }, [christmas]);

  return (
    <StyledRoot
      style={{
        ...(headerHeight ? { height: headerHeight } : {}),
      }}
      className={classNames('Header__wrapper', {
        [className]: className,
        [`Header__wrapper--${device}`]: device,
        'Header__wrapper--backgroundOverlay': backgroundOverlay,
        'Header__wrapper--notificationAlertHidden': !bannerActive,
        'Header__wrapper--notificationAlertShow': bannerActive,
      })}
    >
      <div
        ref={ref => {
          setHeaderRef(ref);
        }}
        className={classNames('Header__root', {})}
        style={{ backgroundColor: '#ffffff' }}
      >
        <StyledContainer
          type="fullWidth"
          style={{
            ...(style || {}),
            ...{
              ...(backgroundOverlay
                ? {
                    backgroundImage: `url(${backgroundOverlay})`,
                  }
                : {}),
            },
          }}
        >
          {children}
        </StyledContainer>
        {mobile && (
          <>
            <Divider thin margin="0" />
            <StyledContainer width="100%">
              <SearchBar id="navbarDesktopSearch" type="navbar" />
            </StyledContainer>
          </>
        )}
      </div>
    </StyledRoot>
  );
};

export default Wrapper;
